import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiDelete, apiGet, apiPatch, apiPost, apiPut } from '@/lib/api';
import type {
  MerchCheckoutDto,
  MerchCheckoutResponse,
  MerchOrder,
  MerchProduct,
  MerchVariantInput,
  PublicMerchProductDetail,
  PublicMerchStore,
  UpdateMerchDetailsDto,
} from '@/modules/merch/interfaces/merch';

const queryKeys = {
  publicStore: (slug: string) => ['merch', 'public', slug] as const,
  publicProduct: (slug: string, productId: string) =>
    ['merch', 'public', slug, 'product', productId] as const,
  adminProducts: (orgId: string) => ['merch', 'admin', orgId, 'products'] as const,
  adminProduct: (orgId: string, productId: string) =>
    ['merch', 'admin', orgId, 'product', productId] as const,
  adminOrders: (orgId: string) => ['merch', 'admin', orgId, 'orders'] as const,
};

export const usePublicMerchQuery = (slug?: string, initialData?: PublicMerchStore) =>
  useQuery({
    queryKey: queryKeys.publicStore(slug || ''),
    queryFn: () => apiGet<PublicMerchStore>(`/public/merch/${slug}`),
    enabled: Boolean(slug),
    initialData,
    staleTime: initialData ? 5 * 60 * 1000 : 0,
  });

export const usePublicMerchProductDetailQuery = (
  slug?: string,
  productId?: string,
  initialData?: PublicMerchProductDetail,
) =>
  useQuery({
    queryKey: queryKeys.publicProduct(slug || '', productId || ''),
    queryFn: () => apiGet<PublicMerchProductDetail>(`/public/merch/${slug}/products/${productId}`),
    enabled: Boolean(slug && productId),
    initialData,
    staleTime: initialData ? 5 * 60 * 1000 : 0,
  });

export const useMerchCheckoutMutation = (slug: string) =>
  useMutation({
    mutationFn: (payload: MerchCheckoutDto) =>
      apiPost<MerchCheckoutResponse>(`/public/merch/${slug}/checkout`, payload),
  });

export const useMerchProductsQuery = (orgId?: string) =>
  useQuery({
    queryKey: queryKeys.adminProducts(orgId || ''),
    queryFn: () => apiGet<MerchProduct[]>(`/organizations/${orgId}/merch/products`),
    enabled: Boolean(orgId),
  });

export const useMerchProductDetailQuery = (orgId?: string, productId?: string) =>
  useQuery({
    queryKey: queryKeys.adminProduct(orgId || '', productId || ''),
    queryFn: () => apiGet<MerchProduct>(`/organizations/${orgId}/merch/products/${productId}`),
    enabled: Boolean(orgId && productId),
  });

export const useMerchOrdersQuery = (orgId?: string) =>
  useQuery({
    queryKey: queryKeys.adminOrders(orgId || ''),
    queryFn: () => apiGet<MerchOrder[]>(`/organizations/${orgId}/merch/orders`),
    enabled: Boolean(orgId),
  });

export const useCreateMerchProductMutation = (orgId?: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (formData: FormData) =>
      apiPost<MerchProduct>(`/organizations/${orgId}/merch/products`, formData),
    onSuccess: () => {
      if (orgId) {
        void queryClient.invalidateQueries({ queryKey: queryKeys.adminProducts(orgId) });
      }
    },
  });
};

export const useUpdateMerchProductMutation = (orgId?: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ productId, formData }: { productId: string; formData: FormData }) =>
      apiPatch<MerchProduct>(`/organizations/${orgId}/merch/products/${productId}`, formData),
    onSuccess: (_data, variables) => {
      if (orgId) {
        void queryClient.invalidateQueries({ queryKey: queryKeys.adminProducts(orgId) });
        void queryClient.invalidateQueries({
          queryKey: queryKeys.adminProduct(orgId, variables.productId),
        });
      }
    },
  });
};

export const useDeleteMerchProductMutation = (orgId?: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (productId: string) =>
      apiDelete<MerchProduct>(`/organizations/${orgId}/merch/products/${productId}`),
    onSuccess: () => {
      if (orgId) {
        void queryClient.invalidateQueries({ queryKey: queryKeys.adminProducts(orgId) });
      }
    },
  });
};

export const useUpdateMerchVariantsMutation = (orgId?: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      productId,
      variants,
    }: {
      productId: string;
      variants: MerchVariantInput[];
    }) =>
      apiPut<MerchProduct>(`/organizations/${orgId}/merch/products/${productId}/variants`, {
        variants,
      }),
    onSuccess: (_data, variables) => {
      if (orgId) {
        void queryClient.invalidateQueries({ queryKey: queryKeys.adminProducts(orgId) });
        void queryClient.invalidateQueries({
          queryKey: queryKeys.adminProduct(orgId, variables.productId),
        });
      }
    },
  });
};

export const useUpdateMerchDetailsMutation = (orgId?: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      productId,
      details,
    }: {
      productId: string;
      details: UpdateMerchDetailsDto;
    }) =>
      apiPatch<MerchProduct>(`/organizations/${orgId}/merch/products/${productId}/details`, details),
    onSuccess: (_data, variables) => {
      if (orgId) {
        void queryClient.invalidateQueries({ queryKey: queryKeys.adminProducts(orgId) });
        void queryClient.invalidateQueries({
          queryKey: queryKeys.adminProduct(orgId, variables.productId),
        });
      }
    },
  });
};

export const useUploadMerchImageMutation = (orgId?: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      productId,
      formData,
    }: {
      productId: string;
      formData: FormData;
    }) =>
      apiPost(`/organizations/${orgId}/merch/products/${productId}/images`, formData),
    onSuccess: (_data, variables) => {
      if (orgId) {
        void queryClient.invalidateQueries({ queryKey: queryKeys.adminProducts(orgId) });
        void queryClient.invalidateQueries({
          queryKey: queryKeys.adminProduct(orgId, variables.productId),
        });
      }
    },
  });
};

export const useDeleteMerchImageMutation = (orgId?: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ productId, imageId }: { productId: string; imageId: string }) =>
      apiDelete(`/organizations/${orgId}/merch/products/${productId}/images/${imageId}`),
    onSuccess: (_data, variables) => {
      if (orgId) {
        void queryClient.invalidateQueries({ queryKey: queryKeys.adminProducts(orgId) });
        void queryClient.invalidateQueries({
          queryKey: queryKeys.adminProduct(orgId, variables.productId),
        });
      }
    },
  });
};

export const useToggleMerchProductMutation = (orgId?: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ productId, isMerch }: { productId: string; isMerch: boolean }) =>
      apiPatch<MerchProduct>(`/organizations/${orgId}/merch/products/${productId}/merch`, {
        is_merch: isMerch,
      }),
    onSuccess: () => {
      if (orgId) {
        void queryClient.invalidateQueries({ queryKey: queryKeys.adminProducts(orgId) });
      }
    },
  });
};
